import {
  type Anchor,
  type NonZeroVector,
  type Point,
  type Segment,
  type Vector,
  point,
  quadraticBezierCurve,
  segment,
  vector,
} from '../elements';
import {
  EDGE_ARROW_HEAD_HEIGHT,
  EDGE_HIT_AREA_THICKNESS,
  EDGE_SHIFT_MULTIPLIER,
  EDGE_TIP_ELEMENT_DISTANCE_FROM_NODE,
  MAX_MAIN_SEGMENT_DISTANCE,
} from '../graphConstants';
import {
  getNodeDataClippingPoint,
  getNodeDataHitArea,
  nodeTypeIcons,
} from '../nodes/icons/icons';
import type { NodeData } from '../nodes/types';
import { getGraphFonts } from '../svg/text';
import {
  convertRadiansToIntegerDegrees,
  faceRight,
  isFacingRight,
} from '../utils/angles';
import {
  type EdgeLabelValue,
  type EdgeLabels,
  getEdgeCenterLabelText,
  getEdgeLinecapLabelText,
} from './EdgeLabels';
import {
  EDGE_CIRCLE_RADIUS,
  type EdgePositions,
  type EdgePositionsCircle,
} from './EdgePositions';
import {
  EDGE_LABEL_FONT_SIZE,
  type EdgeCenterLabel,
  type EdgeLinecapLabel,
} from './constants';
import { type EdgeDataForPositions, getArrowHeadPoints } from './positions';

export const segmentLabelCenterAnchor: Anchor = { x: 0.5, y: 0.5 };
export const segmentLabelEndAnchor: Anchor = { x: 1, y: 0.5 };
export const segmentLabelStartAnchor: Anchor = { x: 0, y: 0.5 };

const getSegmentVisibleLabels = (
  labels: EdgeLabels,
  segmentLength: number,
): { [key in keyof EdgeLabels]: boolean } => {
  const getLabelSize = <T extends EdgeCenterLabel | EdgeLinecapLabel>(
    labelValues: EdgeLabelValue<T>[] | undefined,
    getText: (l: EdgeLabelValue<T>[]) => string | null,
  ): number => {
    if (labelValues === undefined) {
      return 0;
    }
    const text = getText(labelValues);
    return text === null
      ? 0
      : getGraphFonts().edgeLabels.getWidth(text, {
          fontSize: EDGE_LABEL_FONT_SIZE,
        });
  };

  const sourceSize = getLabelSize(labels.source, getEdgeLinecapLabelText);
  const centerSize = getLabelSize(labels.center, getEdgeCenterLabelText);
  const targetSize = getLabelSize(labels.target, getEdgeLinecapLabelText);
  const linecapsOverlap = sourceSize + targetSize > segmentLength;
  const source = sourceSize <= segmentLength && !linecapsOverlap;
  const target = targetSize <= segmentLength && !linecapsOverlap;
  return {
    center:
      centerSize <= segmentLength &&
      // (half of center) + linecap must be <= half of segment to not overlap,
      // equation multiplied by 2 to replace 2 divisions with one multiplication
      (!source || sourceSize * 2 + centerSize <= segmentLength) &&
      (!target || targetSize * 2 + centerSize <= segmentLength),
    source,
    target,
  };
};

function getSegmentLabels(
  seg: Segment,
  thickness: number,
  labels: EdgeLabels,
): EdgePositions['labels'] | undefined {
  const v = segment.toVector(seg);
  if (!vector.isNonZero(v)) {
    // hide labels on zero segment
    return undefined;
  }
  const visibleLabels = getSegmentVisibleLabels(labels, segment.getLength(seg));

  const origAngle = vector.getAngle(v);
  const facingRight = isFacingRight(origAngle);
  const angle = convertRadiansToIntegerDegrees(faceRight(origAngle));
  const perpVector = vector.perp(v, !facingRight);
  return {
    source: visibleLabels.source
      ? {
          anchor: facingRight ? segmentLabelStartAnchor : segmentLabelEndAnchor,
          angle,
          position: point.createOnVector(
            seg.start,
            perpVector,
            thickness * 0.5,
          ),
        }
      : undefined,
    center: visibleLabels.center
      ? {
          anchor: segmentLabelCenterAnchor,
          angle,
          position: point.createOnVector(
            segment.getPoint(seg, 0.5),
            perpVector,
            thickness * 0.5,
          ),
        }
      : undefined,
    target: visibleLabels.target
      ? {
          anchor: facingRight ? segmentLabelEndAnchor : segmentLabelStartAnchor,
          angle,
          position: point.createOnVector(seg.end, perpVector, thickness * 0.5),
        }
      : undefined,
  };
}

/**
 * Computes arrowheads and shifts starting and ending points of an edge to not visually disturb tips of the arrowheads
 */
function placeEdgeElements(
  edgeData: EdgeDataForPositions,
  startPoint: Point,
  startVector: NonZeroVector,
  endPoint: Point,
  endVector: NonZeroVector,
) {
  if (edgeData.direction === 'bidirected' && edgeData.circle) {
    throw new Error('Rendering circle on bidirected edge is not supported');
  }
  let edgeStart = startPoint;
  let edgeEnd = endPoint;
  let circle: EdgePositionsCircle | undefined;
  const arrowHeads: EdgePositions['arrowHeads'] = {};
  const arrowWidth = EDGE_HIT_AREA_THICKNESS + edgeData.thickness;
  const arrowHeight = EDGE_ARROW_HEAD_HEIGHT + edgeData.thickness;

  if (edgeData.direction !== 'undirected') {
    edgeEnd = point.createOnVector(
      edgeEnd,
      endVector,
      EDGE_TIP_ELEMENT_DISTANCE_FROM_NODE,
    );
    arrowHeads.target = getArrowHeadPoints(
      edgeEnd,
      endVector,
      arrowWidth,
      arrowHeight,
    );
    edgeEnd = point.createOnVector(edgeEnd, endVector, arrowHeight);
    if (edgeData.direction === 'bidirected') {
      edgeStart = point.createOnVector(
        edgeStart,
        startVector,
        EDGE_TIP_ELEMENT_DISTANCE_FROM_NODE,
      );
      arrowHeads.source = getArrowHeadPoints(
        edgeStart,
        startVector,
        arrowWidth,
        arrowHeight,
      );
      edgeStart = point.createOnVector(edgeStart, startVector, arrowHeight);
    }
  }
  if (edgeData.circle) {
    // Start the edge at the center of the circle - this way the edge line will be
    // nicely attached to the circle without gaps and without overlapping it on the other end.
    edgeStart = point.createOnVector(
      edgeStart,
      startVector,
      EDGE_CIRCLE_RADIUS + EDGE_TIP_ELEMENT_DISTANCE_FROM_NODE,
    );
    circle = {
      radius: EDGE_CIRCLE_RADIUS,
      position: edgeStart,
    };
  }

  return {
    arrowHeads,
    circle,
    edgeStart,
    edgeEnd,
  };
}

function getStraightEdgePositions(
  edgeData: EdgeDataForPositions,
  source: NodeData,
  target: NodeData,
): EdgePositions | null {
  const segmentMetrics = getSegmentMetrics(source, target);
  if (segmentMetrics === null) {
    return null;
  }
  const clippingPointStart = getNodeDataClippingPoint(source)(
    segmentMetrics.normVector,
  );
  const clippingPointEnd = getNodeDataClippingPoint(target)(
    vector.reverse(segmentMetrics.normVector),
  );

  if (
    isPointInHitArea(source, clippingPointEnd) ||
    isPointInHitArea(target, clippingPointStart)
  ) {
    // Bounding boxes overlay each other
    return null;
  }

  if (segmentMetrics.mainSegmentBase === null) {
    // To avoid glitchy render of arrows and be consistent with segmented edges,
    // we render just a simple line for straight edges that are too short to have a main segment part.
    return {
      line: [clippingPointStart, clippingPointEnd],
    };
  }

  // Normalized vectors
  const vector180 = vector.reverse(segmentMetrics.normVector);

  const { arrowHeads, circle, edgeStart, edgeEnd } = placeEdgeElements(
    edgeData,
    clippingPointStart,
    segmentMetrics.normVector,
    clippingPointEnd,
    vector180,
  );

  return {
    arrowHeads,
    circle,
    labels:
      segmentMetrics.mainSegmentBase && edgeData.labels
        ? getSegmentLabels(
            segmentMetrics.mainSegmentBase,
            edgeData.thickness,
            edgeData.labels,
          )
        : undefined,
    line: [edgeStart, edgeEnd],
  };
}

interface SegmentMetrics {
  /**
   * Main segment as if it was positioned to base edge (shift = 0)
   *
   * null means the edge is too short to render the main segment.
   */
  mainSegmentBase: Segment | null;
  /** Normalized vector representing main segment direction */
  normVector: NonZeroVector;
}

function getSegmentMetrics(
  source: NodeData,
  target: NodeData,
): SegmentMetrics | null {
  const centerDistance = point.getDistance(source.position, target.position);
  const getNodeMainSegmentDistance = (node: NodeData): number =>
    // Computes main segment distance as 1/8th of distance between close nodes to straighten edge
    // 1/8th is chosen for smooth straightening, half will generate sharp edge that looks broken
    // MAX_MAIN_SEGMENT_DISTANCE is used for nodes in safe distance.
    Math.min(centerDistance / 8, MAX_MAIN_SEGMENT_DISTANCE) +
    // consider node dimensions to prevent main segment hiding behind the node
    nodeTypeIcons[node.type].getMaxRadius(node.style);

  const segmentVectorRaw = vector.fromAtoB(source.position, target.position);
  if (!vector.isNonZero(segmentVectorRaw)) {
    return null;
  }
  const normVector = vector.normalize(segmentVectorRaw);

  const sourceMainSegmentDistance = getNodeMainSegmentDistance(source);
  const targetMainSegmentDistance = getNodeMainSegmentDistance(target);
  if (sourceMainSegmentDistance + targetMainSegmentDistance >= centerDistance) {
    // cannot render edges so short that they'd have a non-positive main segment length
    return {
      mainSegmentBase: null,
      normVector,
    };
  }

  const getMainSegmentPoint = (node: NodeData, distance: number): Point =>
    point.createOnVector(node.position, vector.mult(normVector, distance));

  return {
    mainSegmentBase: segment.create(
      getMainSegmentPoint(source, sourceMainSegmentDistance),
      getMainSegmentPoint(target, -targetMainSegmentDistance),
    ),
    normVector,
  };
}

function getShortShiftedEdgePositions(
  source: NodeData,
  target: NodeData,
  shiftVector: Vector,
): EdgePositions | null {
  const segmentHalfPoint = segment.getPoint(
    segment.create(source.position, target.position),
    0.5,
  );

  const controlPoint = point.createOnVector(segmentHalfPoint, shiftVector);
  // following vectors are nonzero since controlPoint is at half of [source, target] segment
  const startPoint = getNodeDataClippingPoint(source)(
    vector.fromAtoB(source.position, controlPoint) as NonZeroVector,
  );
  const endPoint = getNodeDataClippingPoint(target)(
    vector.fromAtoB(target.position, controlPoint) as NonZeroVector,
  );
  if (
    isPointInHitArea(source, endPoint) ||
    isPointInHitArea(target, startPoint)
  ) {
    // Bounding boxes overlay each other
    return null;
  }
  return {
    line: [
      startPoint,
      quadraticBezierCurve.createLinePart(controlPoint, endPoint),
    ],
  };
}

function getSegmentedEdgePositions(
  edgeData: EdgeDataForPositions,
  source: NodeData,
  target: NodeData,
): EdgePositions | null {
  const segmentMetrics = getSegmentMetrics(source, target);
  if (segmentMetrics === null) {
    return null;
  }

  const vector90 = vector.perp(segmentMetrics.normVector);

  /**
   * Edges A->B and B->A have opposite direction, so edge A->B with shift -1
   * would render over edge B->A with shift 1 if implemented naively.
   * So, we flip shift in one of orientations to solve this.
   */
  const orientationCoefficient = source.sn > target.sn ? -1 : 1;
  const shiftDistance =
    orientationCoefficient * edgeData.shift * EDGE_SHIFT_MULTIPLIER;
  const shiftVector = vector.mult(vector90, shiftDistance);

  if (segmentMetrics.mainSegmentBase === null) {
    return getShortShiftedEdgePositions(source, target, shiftVector);
  }

  // Compute control points by shift vectors
  const controlStart = point.createOnVector(
    segmentMetrics.mainSegmentBase.start,
    shiftVector,
  );
  const controlEnd = point.createOnVector(
    segmentMetrics.mainSegmentBase.end,
    shiftVector,
  );

  const startVector = vector.fromAtoB(source.position, controlStart);
  const endVector = vector.fromAtoB(target.position, controlEnd);

  if (!vector.isNonZero(startVector) || !vector.isNonZero(endVector)) {
    return null;
  }

  const clippingPointStart = getNodeDataClippingPoint(source)(startVector);
  const clippingPointEnd = getNodeDataClippingPoint(target)(endVector);
  if (
    isPointInHitArea(source, clippingPointEnd) ||
    isPointInHitArea(target, clippingPointStart)
  ) {
    // Bounding boxes overlay each other
    return null;
  }

  const { arrowHeads, circle, edgeStart, edgeEnd } = placeEdgeElements(
    edgeData,
    clippingPointStart,
    startVector,
    clippingPointEnd,
    endVector,
  );

  return {
    arrowHeads,
    circle,
    labels: edgeData.labels
      ? getSegmentLabels(
          segment.create(controlStart, controlEnd),
          edgeData.thickness,
          edgeData.labels,
        )
      : undefined,
    line: [edgeStart, controlStart, controlEnd, edgeEnd],
  };
}

const isPointInHitArea = (node: NodeData, { x, y }: Point) =>
  getNodeDataHitArea(node).contains(x, y);

export function getEdgeBetweenNodesPositions(
  source: NodeData,
  target: NodeData,
  edgeData: EdgeDataForPositions,
): EdgePositions | null {
  const v = vector.fromAtoB(source.position, target.position);
  if (!vector.isNonZero(v)) {
    // Bounding boxes are directly on top of each other
    return null;
  }

  return edgeData.shift === 0
    ? getStraightEdgePositions(edgeData, source, target)
    : getSegmentedEdgePositions(edgeData, source, target);
}

export const testable = {
  getSegmentLabels,
};
